import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData, getNoSlip } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface 현재 오픈된 메뉴의 메뉴 정보 (CD_MENU, NM_MENU, CD_MODULE...)
import SnButton from "@shared/components/SnButton";
import SN_MAS_SET_REG_003_POP_001 from "@menu/MAS/SN_MAS_SET_REG_003/SN_MAS_SET_REG_003_POP_001";
import SN_MAS_SET_REG_003_POP_002 from "@menu/MAS/SN_MAS_SET_REG_003/SN_MAS_SET_REG_003_POP_002";
import SN_MAS_SET_REG_003_POP_003 from "@menu/MAS/SN_MAS_SET_REG_003/SN_MAS_SET_REG_003_POP_003";
import { showPopupModal } from "@widgets/CommonModal";
import SysPwdPopup from "@widgets/commonpopup/SysPwdPopup";
import layout from "./SN_MAS_SET_REG_003.json";
import ComCreateUi from "@menu/COM/ComCreateUi";
import {
  ColumnConfig,
  RowColumnType,
  SnGridRef,
} from "@shared/components/GridV2/SnGridTypes";

// D → 날짜, H → 코드 도움창
const cdFlagTypeByRow = (data: any): RowColumnType | undefined => {
  switch (data?.tp_ctrl) {
    case "D":
      return "SnDateBox";
    case "H":
      return "SnCodeBox";
    default:
      return "SnTextBox";
  }
};

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

const SN_MAS_SET_REG_003 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region Variable
  const gridApiRef = useRef<any>(null); // 그리드 Reference 변수
  const { userInfo } = global(); // Login user 정보
  //#endregion

  //#region Components useState
  const [isLoading, setIsLoading] = useState(false);
  const [gridFocus, setGridFocus] = useState(false);

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
  });

  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });

  const [headerInfo, setHeaderInfo] = useState<any>({
    grid1: {},
  });
  //#endregion

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();

        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        setFormData(rtn.formData); // 조회조건 초기값
        // H그리드 항목 설정 (cd_flag 는 행의 tp_ctrl 에 따라 입력 방식이 달라진다)
        setColums({
          grid1: (rtn.grid.grid1 || []).map((col: ColumnConfig) =>
            col.cd_column === "cd_flag"
              ? { ...col, tpColumnByRow: cdFlagTypeByRow }
              : col
          ),
        });
        setHeaderInfo({
          grid1: rtn.header.grid1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  //#region Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onSave,
  }));
  //#endregion

  //#region onSearch
  const onSearch = async () => {
    const modifiedRows = getGridCurrent("grid1").getChange();
    if (modifiedRows.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        await onSave();
      }
    }
    try {
      const params = {
        id: "sn_mas_set_reg_003_01",
        params: searchRef.current.getFormdata(),
      };
      setDataSources({ grid1: [] });
      const data: any = await fnGetDataSet(params);
      setDataSources({ grid1: data[0] || [] });

      getGridCurrent("grid1").resetUpdate();
    } catch (error: any) {}
  };
  //#endregion

  //#region onBeforeAddRow
  const onBeforeAddRow = (): any => {
    try {
      const updatedRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
      };

      return { updatedRow };
    } catch (error: any) {
      return false;
    }
  };
  //#endregion
  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return;
      }
      const changesRow = getGridCurrent("grid1").getChange();
      // ✅ 각 구분별 배열에서 cd_flag → dc_ctrl 복사
      const processRows = (rows: any[]) =>
        rows.map((row) =>
          row.cd_flag !== undefined && row.cd_flag !== null
            ? { ...row, dc_ctrl: row.cd_flag }
            : row
        );

      const updatedRows = {
        insert: processRows(changesRow.insert || []),
        update: processRows(changesRow.update || []),
        delete: changesRow.delete || [],
      };

      const SaveData = {
        sys_config: updatedRows,
      };
      if (changesRow.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return;
      }
      // 저장 API 호출
      await fnSave("SN_MAS_SET_REG_003_SAVE_O1", SaveData);

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
    } catch (error: any) {
      console.log("d");
    }
  };
  //#endregion

  //#region onGrid1BtnClick
  const onGrid1BtnsClick = async (id: string) => {
    if (id == "btnSms") {
      // if (getGridCurrent("grid1").api.getDisplayedRowCount() === 0) return;
      const component = <SN_MAS_SET_REG_003_POP_002 />;
      const result = await showPopupModal({
        title: "SMS상용구설정 도움",
        children: component,
        width: "45%",
        validation: true,
      });
      if (!result || result === "close") return;
    }

    if (id == "btnIp") {
      //if (getGridCurrent("grid1").api.getDisplayedRowCount() === 0) return;
      const component = <SN_MAS_SET_REG_003_POP_003 />;
      const result = await showPopupModal({
        title: "허용IP관리 도움",
        children: component,
        validation: true,
        height: "65%",
        width: "40%",
      });
      if (!result || result === "close") return;
    }

    if (id == "btnFormat") {
      const selectedNode = getGridCurrent("grid1").focusedRowNode();
      if (selectedNode && selectedNode.data?.state === "insert") {
        showDialog("alter", "저장 후 입력 가능합니다.");
        return;
      }
      const component = <SN_MAS_SET_REG_003_POP_001 />;
      const result = await showPopupModal({
        title: "포맷설정 도움",
        children: component,
        height: "55%",
        width: "25%",
        okayBtn: true,
        validation: true,
      });
      if (!result || result === "close") return;
    }

    if (id == "btnReset") {
      // 전체초기화
      const modifiedRows = getGridCurrent("grid1").getChange();
      if (modifiedRows.modifiedRowcount > 0) {
        await showDialog(
          "alter",
          "수정중인 건을 저장 후 진행해주시기 바랍니다."
        );
        return;
      }

      const confirm = await showDialog(
        "confirm",
        "추가 작성한 내용이 모두 삭제됩니다.\n초기설정으로 전체 초기화 하시겠습니까?"
      );
      if (!confirm) {
        return;
      }

      const component = <SysPwdPopup />;
      const result = await showPopupModal({
        title: "SYS계정 암호를 입력해주세요.",
        children: component,
        width: "390px",
        height: "210px",
        checkData: false,
      });
      if (!result || result === "close") return;

      const params = { cd_firm: userInfo.cd_firm };
      const reset = await callApi("SN_MAS_SET_REG_003_btnReset", params);
      if (reset.status) {
        onSearch();
      }
    }

    if (id === "btnApply") {
      //추가사항적용
      const modifiedRows = getGridCurrent("grid1").getChange();
      if (modifiedRows.modifiedRowcount > 0) {
        await showDialog(
          "alter",
          "수정중인 건을 저장 후 진행해주시기 바랍니다."
        );
        return;
      }

      const confirm = await showDialog(
        "confirm",
        "추가사항을 적용 하시겠습니까?"
      );
      if (!confirm) return;

      const params = { cd_firm: userInfo.cd_firm };
      const result = await callApi("SN_MAS_SET_REG_003_btnApply", params);
      if (result.status) {
        onSearch();
      }
    }
  };
  //#endregion
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRow,
          onBtnEtcClick: onGrid1BtnsClick,
        },
        headerInfo: headerInfo.grid1,
        filter: { refs: ["grid2"], columns: ["no_so"] },
      },
    }),
    [columns, dataSources, headerInfo]
  );

  const search = useMemo(
    () => ({
      data: formData,
      controls: formControlData,
    }),
    [formData, formControlData]
  );

  const dynamicProps = React.useMemo(
    () => ({
      searchRef,
      search,
      grids,
      gridRefs,
    }),
    [formData, formControlData, grids, gridRefs]
  );
  //region UI
  return <ComCreateUi dynamicProps={dynamicProps} layoutConfig={layout} />;
});
export default SN_MAS_SET_REG_003;
